import { defineComponent as _defineComponent } from 'vue'
import { ActionType, handleAction } from '@@/bits/whiteboard'
import OzBox, { OzBoxColors } from '@@/library/v4/components/OzBox.vue'
import { useContentPickerStore } from '@@/pinia/content_picker'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceContainerSizeStore } from '@@/pinia/surface_container_size'
import { useSurfaceContentPickerStore } from '@@/pinia/surface_content_picker'
import { useSurfaceWindowMobileNotchStore } from '@@/pinia/surface_window_mobile_notch'
import { useWhiteboardStore } from '@@/pinia/whiteboard'
import { useWhiteboardPermissionsStore } from '@@/pinia/whiteboard_permissions'
import { useWhiteboardToolbarStore } from '@@/pinia/whiteboard_toolbar'
import WhiteboardToolbarIcon from '@@/vuecomponents/WhiteboardToolbarIcon.vue'
import { storeToRefs } from 'pinia'
import { computed, nextTick, ref, watch } from 'vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'WhiteboardToolbar',
  setup(__props, { expose }) {

const surfaceStore = useSurfaceStore()
const whiteboardStore = useWhiteboardStore()

const { currentFrameId, selectedSecondaryTool, isLoadingPortraitMobileLayout } = storeToRefs(whiteboardStore)
const whiteboardPermissionsStore = useWhiteboardPermissionsStore()
const { frameIdToFramePermissionOverride } = storeToRefs(whiteboardPermissionsStore)
const whiteboardToolbarStore = useWhiteboardToolbarStore()

const surfaceContainerSizeStore = useSurfaceContainerSizeStore()
const { isContainerSmallerThanTabletPortrait, isContainerSmallerThanTabletLandscape } =
  storeToRefs(surfaceContainerSizeStore)

const isSingleColumnLayout = computed(() => {
  return isContainerSmallerThanTabletLandscape.value && !isLoadingPortraitMobileLayout.value
})

const isDoubleColumnLayout = computed(() => {
  return !isContainerSmallerThanTabletLandscape.value && !isLoadingPortraitMobileLayout.value
})

const isSingleRowLayout = computed(() => {
  return isLoadingPortraitMobileLayout.value
})
/**
 * COLOR SCHEME
 * Currently follow background luminance of surface (based on walllpaper)
 */
const colorScheme = computed((): 'dark' | 'light' => {
  return surfaceStore.backgroundLuminance
})

// Tooltip

const { trackToolHoverOn, trackToolHoverOff } = whiteboardToolbarStore
const { xTooltip, tooltipToolId } = storeToRefs(whiteboardToolbarStore)

/**
 * TOOL LOGIC
 */

const toolDisabledState = computed(() => ({
  undo: !whiteboardStore.canIUndo,
  redo: !whiteboardStore.canIRedo,
}))
function handleSelectTool(event: PointerEvent, toolId: string): void {
  // Attachment tool will switch tldraw to select tool and then show the content picker
  if (toolId === 'padlet-upload') {
    whiteboardStore.selectTool('select')
    useSurfaceContentPickerStore().showPostContentPicker()
    useContentPickerStore().showContentSourceMenu({
      isHiddenMenu: true,
      contentPickerContext: 'whiteboardUpload',
    })
    nextTick(() => {
      useContentPickerStore().triggerFileBrowser()
    })
    return
  }
  if (toolId === 'padlet-attachment') {
    whiteboardStore.selectTool('select')
    useSurfaceContentPickerStore().showPostContentPicker()
    useContentPickerStore().showContentSourceMenu({ contentPickerContext: 'whiteboardUpload' })

    return
  }
  if (toolId === 'undo') {
    handleAction(ActionType.Undo, () => whiteboardStore.canIUndo)
    return
  }
  if (toolId === 'redo') {
    handleAction(ActionType.Redo, () => whiteboardStore.canIRedo)
    return
  }
  // Use currentTarget, not target, otherwise the selected element can vary
  whiteboardStore.setSelectedToolbarButtonEl(event.currentTarget as HTMLElement)
  whiteboardStore.selectTool(toolId)
}

const getToolsToShow = () =>
  whiteboardStore.tools.filter((tool) => {
    if (tool.id === 'draw-nested') {
      const childrenToolIds = tool.secondaryMenu?.items.map((item) => item.id)
      return childrenToolIds?.some((id) =>
        whiteboardPermissionsStore.isToolAllowed(id, whiteboardStore.currentFrameId!),
      )
    }
    return whiteboardPermissionsStore.isToolAllowed(tool.id, whiteboardStore.currentFrameId!)
  })

const toolsToShow = ref(getToolsToShow())

expose({ toolsToShow })

watch([frameIdToFramePermissionOverride, currentFrameId, selectedSecondaryTool], () => {
  // Explanation for each watched value:
  // frameIdToFramePermissionOverride: Permissions can update in realtime and change the tools to show
  // currentFrameId: Certain frames can have different permissions, i.e. different tools to show
  // selectedSecondaryTool: Update the tool icon color on the toolbar
  toolsToShow.value = getToolsToShow()
})

const toolbar = ref<HTMLElement | null>(null)

watch(
  isContainerSmallerThanTabletPortrait,
  (isSmaller) => {
    if (isSmaller) {
      nextTick(() => {
        if (toolbar.value) {
          toolbar.value.scrollLeft = toolbar.value.scrollWidth / 2 - toolbar.value.clientWidth / 2
          toolbar.value.scrollTop = toolbar.value.scrollHeight / 2 - toolbar.value.clientHeight / 2
        }
      })
    }
  },
  { immediate: true },
)

const surfaceWindowMobileNotchStore = useSurfaceWindowMobileNotchStore()

return { __sfc: true,surfaceStore, whiteboardStore, currentFrameId, selectedSecondaryTool, isLoadingPortraitMobileLayout, whiteboardPermissionsStore, frameIdToFramePermissionOverride, whiteboardToolbarStore, surfaceContainerSizeStore, isContainerSmallerThanTabletPortrait, isContainerSmallerThanTabletLandscape, isSingleColumnLayout, isDoubleColumnLayout, isSingleRowLayout, colorScheme, trackToolHoverOn, trackToolHoverOff, xTooltip, tooltipToolId, toolDisabledState, handleSelectTool, getToolsToShow, toolsToShow, toolbar, surfaceWindowMobileNotchStore, OzBox, OzBoxColors, WhiteboardToolbarIcon }
}

})